import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import {
  ContactDirectoryItem,
  ContactDirectoryPage,
  ContactDirectorySyncPage,
} from '@/models/contactDirectory'
import api from '@/shared/api'
import { ENDPOINTS, UTILS } from '@/shared/constants'
import { RootState } from '..'

const pageSize = 50
const syncPageSize = 200
const cacheVersion = 2
const cachePrefix = 'contact-directory-cache'

interface ContactDirectoryScope {
  companyId: number
  userId: number
}

export interface ContactDirectoryCache extends ContactDirectoryScope {
  version: typeof cacheVersion
  search: string
  items: ContactDirectoryItem[]
  nextCursor: string | null
  hasMore: boolean
  syncCursor?: string
}

interface ContactDirectoryState {
  active: boolean
  search: string
  items: ContactDirectoryItem[]
  nextCursor: string | null
  hasMore: boolean
  loading: boolean
  loadingMore: boolean
  syncing: boolean
  requestId?: string
  requestSearch?: string
  syncRequestId?: string
  syncCursor?: string
  scope?: ContactDirectoryScope
  error: boolean
}

const initialState: ContactDirectoryState = {
  active: false,
  search: UTILS.BLANK,
  items: [],
  nextCursor: null,
  hasMore: false,
  loading: false,
  loadingMore: false,
  syncing: false,
  error: false,
}

function normalizeSearch(search?: string) {
  return search?.trim().toLowerCase() ?? UTILS.BLANK
}

function cacheKey(companyId: number, userId: number, search: string) {
  return `${cachePrefix}:${companyId}:${userId}:${encodeURIComponent(
    normalizeSearch(search)
  )}`
}

export function readContactDirectoryCache(
  companyId: number,
  userId: number,
  search: string
): ContactDirectoryCache | undefined {
  if (typeof window === 'undefined') return undefined

  try {
    const raw = localStorage.getItem(cacheKey(companyId, userId, search))
    if (!raw) return undefined
    const cache = JSON.parse(raw) as ContactDirectoryCache

    if (
      cache.version !== cacheVersion ||
      cache.companyId !== companyId ||
      cache.userId !== userId ||
      cache.search !== normalizeSearch(search) ||
      !Array.isArray(cache.items)
    ) {
      localStorage.removeItem(cacheKey(companyId, userId, search))
      return undefined
    }

    return cache
  } catch {
    localStorage.removeItem(cacheKey(companyId, userId, search))
    return undefined
  }
}

export function persistContactDirectoryCache(
  state: ContactDirectoryState
): void {
  if (typeof window === 'undefined' || !state.scope || state.loading) return

  const cache: ContactDirectoryCache = {
    version: cacheVersion,
    ...state.scope,
    search: normalizeSearch(state.search),
    items: state.items,
    nextCursor: state.nextCursor,
    hasMore: state.hasMore,
    syncCursor: state.syncCursor,
  }
  try {
    localStorage.setItem(
      cacheKey(state.scope.companyId, state.scope.userId, state.search),
      JSON.stringify(cache)
    )
  } catch {
    // O cache é uma otimização; indisponibilidade de storage não bloqueia a tela.
  }
}

function matchesSearch(item: ContactDirectoryItem, search: string) {
  const normalized = normalizeSearch(search)
  if (!normalized) return true

  return [
    item.contactName,
    item.cellPhone,
    item.client.companyName,
    item.client.internalCode,
  ].some((value) => value?.toLowerCase().includes(normalized))
}

function compareItems(left: ContactDirectoryItem, right: ContactDirectoryItem) {
  return (
    left.client.companyName.localeCompare(right.client.companyName, 'pt-BR', {
      sensitivity: 'base',
    }) ||
    left.contactName.localeCompare(right.contactName, 'pt-BR', {
      sensitivity: 'base',
    }) ||
    left.id - right.id
  )
}

export const fetchContactDirectoryPage = createAsyncThunk<
  ContactDirectoryPage,
  ContactDirectoryScope & { search: string; cursor?: string; force?: boolean },
  { state: RootState }
>(
  'contactDirectory/fetchPage',
  async ({ search, cursor }, thunkAPI) => {
    const response = await api.get<ContactDirectoryPage>(
      ENDPOINTS.CONTACTS.GET.DIRECTORY,
      {
        params: {
          limit: pageSize,
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(cursor ? { cursor } : {}),
        },
        signal: thunkAPI.signal,
      }
    )
    return response.data
  },
  {
    condition: ({ companyId, userId, search, cursor }, { getState }) => {
      const state = getState()
      const directory = state.contactDirectory
      if (
        state.auth.user?.companyId !== companyId ||
        state.auth.user.id !== userId ||
        (directory.requestId &&
          directory.requestSearch === normalizeSearch(search))
      ) {
        return false
      }
      return !cursor || directory.nextCursor === cursor
    },
  }
)

interface SyncResult {
  upserts: ContactDirectoryItem[]
  removedIds: number[]
  cursor: string
  fromCursor: string
  resetRequired: boolean
}

export const syncContactDirectory = createAsyncThunk<
  SyncResult,
  void,
  { state: RootState }
>(
  'contactDirectory/sync',
  async (_, thunkAPI) => {
    const initialCursor = thunkAPI.getState().contactDirectory.syncCursor
    if (!initialCursor) throw new Error('Contact directory cursor unavailable')

    const upserts = new Map<number, ContactDirectoryItem>()
    const removedIds = new Set<number>()
    const visited = new Set([initialCursor])
    let cursor = initialCursor

    do {
      const response = await api.get<ContactDirectorySyncPage>(
        ENDPOINTS.CONTACTS.GET.DIRECTORY_SYNC,
        {
          params: { after: cursor, limit: syncPageSize },
          signal: thunkAPI.signal,
        }
      )
      const page = response.data

      if (page.resetRequired) {
        return {
          upserts: [],
          removedIds: [],
          cursor: initialCursor,
          fromCursor: initialCursor,
          resetRequired: true,
        }
      }

      page.upserts.forEach((item) => {
        removedIds.delete(item.id)
        upserts.set(item.id, item)
      })
      page.removedIds.forEach((id) => {
        upserts.delete(id)
        removedIds.add(id)
      })

      if (!page.hasMore) {
        cursor = page.cursor
        break
      }
      if (page.cursor === cursor || visited.has(page.cursor)) {
        throw new Error('Invalid contact directory sync pagination')
      }
      visited.add(page.cursor)
      cursor = page.cursor
    } while (!thunkAPI.signal.aborted)

    return {
      upserts: [...upserts.values()],
      removedIds: [...removedIds],
      cursor,
      fromCursor: initialCursor,
      resetRequired: false,
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState().contactDirectory
      return Boolean(state.active && state.syncCursor && !state.syncRequestId)
    },
  }
)

export const reconcileContactDirectory = createAsyncThunk<
  void,
  ContactDirectoryScope & { search: string },
  { state: RootState }
>('contactDirectory/reconcile', async (args, thunkAPI) => {
  const directory = thunkAPI.getState().contactDirectory
  if (directory.requestId || directory.syncRequestId) return

  if (!directory.syncCursor) {
    await thunkAPI.dispatch(fetchContactDirectoryPage(args)).unwrap()
    return
  }

  try {
    const result = await thunkAPI.dispatch(syncContactDirectory()).unwrap()
    if (!result.resetRequired) return

    await thunkAPI
      .dispatch(fetchContactDirectoryPage({ ...args, force: true }))
      .unwrap()
  } catch {
    if (!thunkAPI.signal.aborted && !directory.items.length) {
      toast.error('Não foi possível atualizar os contatos')
    }
  }
})

const contactDirectorySlice = createSlice({
  name: 'contactDirectory',
  initialState,
  reducers: {
    setContactDirectoryActive(state, action: PayloadAction<boolean>) {
      state.active = action.payload
    },
    hydrateContactDirectory(
      state,
      action: PayloadAction<
        ContactDirectoryScope & {
          search: string
          cache?: ContactDirectoryCache
        }
      >
    ) {
      const { companyId, userId, search, cache } = action.payload
      state.scope = { companyId, userId }
      state.search = normalizeSearch(search)
      state.items = cache?.items ?? []
      state.nextCursor = cache?.nextCursor ?? null
      state.hasMore = cache?.hasMore ?? false
      state.syncCursor = cache?.syncCursor
      state.error = false
    },
    clearContactDirectoryError(state) {
      state.error = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContactDirectoryPage.pending, (state, action) => {
        state.requestId = action.meta.requestId
        state.requestSearch = normalizeSearch(action.meta.arg.search)
        state.loading = !action.meta.arg.cursor
        state.loadingMore = Boolean(action.meta.arg.cursor)
        state.error = false
      })
      .addCase(fetchContactDirectoryPage.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.requestId = undefined
        state.requestSearch = undefined
        state.loading = false
        state.loadingMore = false
        if (
          state.scope?.companyId !== action.meta.arg.companyId ||
          state.scope.userId !== action.meta.arg.userId ||
          normalizeSearch(state.search) !== normalizeSearch(action.meta.arg.search)
        ) {
          return
        }
        const existing = action.meta.arg.cursor ? state.items : []
        const byId = new Map(existing.map((item) => [item.id, item]))
        action.payload.items.forEach((item) => byId.set(item.id, item))
        state.items = [...byId.values()].sort(compareItems)
        state.nextCursor = action.payload.nextCursor
        state.hasMore = action.payload.hasMore
        state.syncCursor = action.payload.syncCursor
      })
      .addCase(fetchContactDirectoryPage.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.requestId = undefined
        state.requestSearch = undefined
        state.loading = false
        state.loadingMore = false
        if (!action.meta.aborted) state.error = true
      })
      .addCase(syncContactDirectory.pending, (state, action) => {
        state.syncRequestId = action.meta.requestId
        state.syncing = true
      })
      .addCase(syncContactDirectory.fulfilled, (state, action) => {
        if (state.syncRequestId !== action.meta.requestId) return
        state.syncRequestId = undefined
        state.syncing = false
        if (
          action.payload.resetRequired ||
          state.syncCursor !== action.payload.fromCursor
        ) {
          return
        }

        const removedIds = new Set(action.payload.removedIds)
        const byId = new Map(
          state.items
            .filter((item) => !removedIds.has(item.id))
            .map((item) => [item.id, item])
        )
        action.payload.upserts.forEach((item) => {
          if (matchesSearch(item, state.search)) byId.set(item.id, item)
          else byId.delete(item.id)
        })
        state.items = [...byId.values()].sort(compareItems)
        state.syncCursor = action.payload.cursor
      })
      .addCase(syncContactDirectory.rejected, (state, action) => {
        if (state.syncRequestId !== action.meta.requestId) return
        state.syncRequestId = undefined
        state.syncing = false
      })
  },
})

export const {
  clearContactDirectoryError,
  hydrateContactDirectory,
  setContactDirectoryActive,
} = contactDirectorySlice.actions
export default contactDirectorySlice.reducer
